import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export function SignaturePad({
  value,
  onChange,
  disabled = false,
  className,
}: {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const hasContentRef = useRef(Boolean(value));
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const isSavingRef = useRef(false);

  const getCtx = () => canvasRef.current?.getContext("2d") || null;

  const getPos = (e: PointerEvent | MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if (e instanceof PointerEvent || e instanceof MouseEvent) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }
    const tEvent = e as TouchEvent;
    const touch = (tEvent.touches && tEvent.touches[0]) || (tEvent.changedTouches && tEvent.changedTouches[0]);
    if (!touch) return { x: 0, y: 0 };
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    setIsDrawing(true);
    const pos = getPos(e.nativeEvent);
    lastPointRef.current = pos;
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || disabled) return;
    const ctx = getCtx();
    const last = lastPointRef.current;
    if (!ctx || !last) return;
    const { x, y } = getPos(e.nativeEvent);
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastPointRef.current = { x, y };
    hasContentRef.current = true;
  };

  const end = () => {
    setIsDrawing(false);
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas && hasContentRef.current && onChange) {
      const dataUrl = canvas.toDataURL("image/png");
      isSavingRef.current = true;
      onChange(dataUrl);
      setTimeout(() => {
        isSavingRef.current = false;
      }, 50);
    }
  };

  const clear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = getCtx();
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasContentRef.current = false;
    if (onChange) {
      isSavingRef.current = true;
      onChange("");
      setTimeout(() => {
        isSavingRef.current = false;
      }, 50);
    }
  };

  // Load existing signature if value exists (but not when we just saved it ourselves)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;

    // Skip if we're the ones who just saved this value
    if (isSavingRef.current) return;

    const ctx = getCtx();
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // Clear canvas first
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Draw the image at display size
      const rect = canvas.getBoundingClientRect();
      ctx.drawImage(img, 0, 0, rect.width, rect.height);
      hasContentRef.current = true;
    };
    img.src = value;
  }, [value]);

  // Ensure canvas backing store matches CSS size and devicePixelRatio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const setSize = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      // Set the internal pixel size to CSS size * DPR
      const targetWidth = Math.floor(rect.width * ratio);
      const targetHeight = Math.floor(rect.height * ratio);
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = getCtx();
        if (ctx) {
          // Reset transform then apply scaling so draw coords stay in CSS pixels
          ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        }
      }
    };

    setSize();
    window.addEventListener("resize", setSize);
    return () => window.removeEventListener("resize", setSize);
  }, []);

  return (
    <div className={`flex flex-col space-y-2 ${className ?? ""}`}>
      <div className="rounded-md border border-gray-300">
        <canvas
          ref={canvasRef}
          className="h-[200px] w-full touch-none bg-white disabled:opacity-50"
          style={{ cursor: disabled ? "not-allowed" : "crosshair" }}
          onPointerDown={start}
          onPointerMove={draw}
          onPointerUp={end}
          onPointerLeave={end}
        />
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={clear}
          disabled={disabled}
          className="rounded-md border px-3 py-1 text-sm disabled:opacity-50"
        >
          {t("Löschen")}
        </button>
      </div>
    </div>
  );
}
